import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { Button } from 'react-bootstrap';
import {
  BuildingWithConvertedAddresses,
  LatLng,
  OffMarketToastMessage,
} from '../utils/types';
import PageMetaTags from '../BuildingModal/PageMetaTags';
import { getCookie } from '../utils/utils';
import { ListingLocationState } from '../utils/useOpenListing';
import {
  getListingLayerLocation,
  getProfileCloseHistoryDelta,
} from '../utils/profileRouteState';
import { createConversationReadTracker } from '../utils/conversationReadTracker';
import { mergeStoredBuilding } from '../utils/mergeStoredBuilding';
import { geocodeColdSeed, resolveBuildingSeed } from './resolveBuildingSeed';
import { apiRequest } from '../utils/api';
import {
  pendingDraftChatToRestore,
  clearPendingDraftChat,
} from '../utils/pendingDraftChat';
import { useTranslation } from 'react-i18next';
import MapLoadingScreen from '../common/MapLoadingScreen';
import NewLandingPage from './NewLandingPage/NewLandingPage';
import type { AddMyPlaceSeed } from './NewLandingPage/AddMyPlaceFlow/AddMyPlaceFlow';
import type { BuildingPreviewMetaPatch } from '../BuildingPreviewCard/useBuildingPreview';
import { useProfileNavigation } from '../../contexts/ProfileNavigationContext';
import '../../App.scss';
import './AppContent.scss';
import '../FeedEvents/FeedComments.scss';
import { getUserCountryWithCoordinates } from './getUserCountry';
import { usePriceEstimationPolling } from '../../hooks/usePriceEstimationPolling';
import AdminModerationTool from './AdminModerationTool';
import AdminRecommendedPropertiesTool from './AdminRecommendedPropertiesTool';
import AdminDisableUserTool from './AdminDisableUserTool';
import { ModalOutlet } from '../ModalController/ModalOutlet';
import { useModals } from '../../contexts/ModalControllerContext';
import Navigation, { getActiveTab, NavTabId } from '../Navigation/Navigation';
import ActivityToast from '../Navigation/ActivityToast';
import ToolsSheet from '../Navigation/ToolsSheet';
import BoostFlow from '../common/ToolFlows/BoostFlow';
import EstimateFlow from '../common/ToolFlows/EstimateFlow';
import ActivityView from '../ActivityView/ActivityView';
import type { DraftChatContext } from '../MessagesModal/MessagesModal';
import {
  fetchActivity,
  markActivitySeen,
  ActivityResponse,
  ActivitySection,
} from '../ActivityView/activityApi';

// Delay in milliseconds before showing the post-signup marketing consent modal.
// Per JAA-68 product feedback, the modal should feel like a natural part of
// onboarding rather than an aggressive interrupt — a short pause lets the
// homepage UI settle before the modal appears.
const MARKETING_CONSENT_MODAL_DELAY_MS = 600;

// Left-to-right order of the bottom-nav tabs, used to pick the slide direction
// when switching: a higher target index slides in from the right ('forward'),
// a lower one from the left ('back'). (JAA-266)
const NAV_TAB_ORDER: Record<NavTabId, number> = {
  explore: 0,
  activity: 1,
  messages: 2,
  tools: 3,
};
export type NavSlideDir = 'forward' | 'back';
type BuildingPreviewMetaUpdater = (
  buildingId: string,
  patch: BuildingPreviewMetaPatch,
) => void;
type BuildingLikeAnimationHandler = (
  building: BuildingWithConvertedAddresses,
) => void;
type AddMyPlaceLauncher = (seed: AddMyPlaceSeed | null) => void;
type MapFocusHandler = (buildingId: string, coords?: LatLng) => void;
type ShowMapHandler = (center?: LatLng) => void;

// A buying-interest success toast (JAA-315) that is owed to the user but not
// shown yet. Stored rather than kept in memory because the toast's surface (the
// bottom nav) is unmounted on the building page and the listing route, and the
// user may close the app from there: it must still greet them next launch.
type PendingInterestToast = {
  kind: 'area' | 'building';
  id: string;
  /** Whose toast it is, so it never surfaces for the next account to sign in. */
  user: string | null;
};

const PENDING_INTEREST_TOAST_KEY = 'jaakko:pendingInterestToast';

// Read the owed toast for this user, if any. Storage access is wrapped because
// Safari private mode throws on localStorage, and a missing toast is not worth
// breaking the app shell over.
const readPendingInterestToast = (
  user: string,
): PendingInterestToast | null => {
  try {
    const raw = window.localStorage.getItem(PENDING_INTEREST_TOAST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingInterestToast;
    if (!parsed?.id || parsed.user !== user) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writePendingInterestToast = (pending: PendingInterestToast) => {
  try {
    window.localStorage.setItem(
      PENDING_INTEREST_TOAST_KEY,
      JSON.stringify(pending),
    );
  } catch {
    // Non-fatal: the toast still shows this session, just not after a reload.
  }
};

const clearPendingInterestToast = () => {
  try {
    window.localStorage.removeItem(PENDING_INTEREST_TOAST_KEY);
  } catch {
    // Non-fatal.
  }
};

// Storage holds exactly one owed toast, so two interests published in quick
// succession race: the first resolver can come back from /activity after the
// second has replaced the record. It must not then clear the newer record or
// show a count on its behalf, so every resolver re-checks that the record is
// still the one it set out to resolve.
const isStillPending = (pending: PendingInterestToast): boolean => {
  try {
    const raw = window.localStorage.getItem(PENDING_INTEREST_TOAST_KEY);
    if (!raw) return false;
    const stored = JSON.parse(raw) as PendingInterestToast;
    return (
      stored?.id === pending.id &&
      stored.kind === pending.kind &&
      stored.user === pending.user
    );
  } catch {
    return false;
  }
};

/**
 * The SPA shell behind every map route: auth/session state, the nav tabs and
 * their overlays, conversations, and the landing page with its map.
 */
/** The current URL with conversation-id set to the selected conversation, or removed when none is selected. */
const withConversationParam = (selected: string | null) => {
  const url = new URL(window.location.href);
  if (selected) {
    url.searchParams.set('conversation-id', String(selected));
  } else {
    url.searchParams.delete('conversation-id');
  }
  return url.pathname + url.search;
};

const AppContent = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const profileNav = useProfileNavigation();
  // A shared single-listing route renders through the shell too (JAA-306) so it
  // reuses the header + modal stack instead of a standalone page.
  const isListingRoute = location.pathname.startsWith('/listing/');
  const listingLayerLocation = useMemo(
    () => getListingLayerLocation(location, profileNav.isProfileRoute),
    [location, profileNav.isProfileRoute],
  );
  const profileCloseHistoryDelta = useMemo(
    () =>
      profileNav.isProfileRoute
        ? getProfileCloseHistoryDelta(location.state)
        : null,
    [location.state, profileNav.isProfileRoute],
  );
  const showNewLandingPage =
    location.pathname === '/' || profileNav.isProfileRoute || isListingRoute;
  const [profileReturnPathAfterLogin, setProfileReturnPathAfterLogin] =
    useState<string | null>(null);
  const prevPathRef = useRef<string>(
    window.location.pathname + window.location.search,
  );

  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [selectedBuilding, setSelectedBuilding] =
    useState<BuildingWithConvertedAddresses | null>(null);
  const [coldBuildingDeepLinkOpen, setColdBuildingDeepLinkOpen] =
    useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);
  // True from the moment a logout starts until the reload lands. Two things
  // read it: the profile route's "log in to continue" prompt stays quiet on the
  // way out, and the profile view stays on screen with its spinner instead of
  // vanishing the instant the session state flips.
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [mapCenter, setMapCenter] = useState<LatLng>({
    lat: 60.16823797537535,
    lng: 24.936944610005185,
  });
  const [isLocationLoaded, setIsLocationLoaded] = useState(false);
  const [showImageModal, setShowImageModal] = useState<string | null>(null);
  const [countryOfFeedContent, setCountryOfFeedContent] = useState<
    string | null
  >('Finland');
  const [messagesModalVisible, setMessagesModalVisible] = useState(false);
  // Read by openers that run inside effects registered once, so they see whether Messages is open now.
  const messagesModalVisibleRef = useRef(messagesModalVisible);
  messagesModalVisibleRef.current = messagesModalVisible;
  // Set while a chat opener steps Back through listings opened over the chat (JAA-445).
  const returningToChatRef = useRef(false);
  // Profile was opened over the open chat and not yet left: stepping Back would land on Profile, not the chat (JAA-445).
  const profileOverChatRef = useRef(false);

  /**
   * Remember where closing Messages should return to: the current path, unless
   * this is Messages itself, and '/' for Profile (DMs and Profile are sibling
   * views). Called by every opener, including the ones that leave the URL alone
   * (draft, restore, open chat).
   */
  const rememberPathBeforeMessages = () => {
    // An opener firing while Messages is already open (e.g. from a listing over it) must not move the return path.
    if (messagesModalVisibleRef.current) return;
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath.startsWith('/direct-messages')) return;
    prevPathRef.current = currentPath.startsWith('/profile') ? '/' : currentPath;
  };

  /**
   * Go one step Back when the page on screen is a listing the app pushed over
   * an open chat, so a chat button used there returns to the chat instead of
   * opening it hidden under that listing (JAA-445). Never leaves the page the
   * chat was opened over. Returns whether it went back.
   */
  const stepBackToChat = useCallback(() => {
    const { pathname, state } = history.location;
    const pushedByApp = !!(state as ListingLocationState | undefined)
      ?.pushedByApp;
    const chatPage = prevPathRef.current.split('?')[0];
    if (profileOverChatRef.current) return false;
    if (!pathname.startsWith('/listing/') || !pushedByApp) return false;
    if (pathname === chatPage) return false;
    history.goBack();
    return true;
  }, [history]);
  // Draft chat opened by a Could-buy click on a listing with no prior
  // conversation (JAA-275, revised per review). Ephemeral by design: nothing
  // exists server-side until the buyer's first message, so closing it (or the
  // modal) simply discards it.
  const [draftChat, setDraftChat] = useState<DraftChatContext | null>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const conversationsRef = useRef<any[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<
    string | null
  >(null);
  const selectedConversationRef = useRef<string | null>(null);
  // Opening a chat marks it read here immediately; the server's own count
  // catches up on the next fetch. See conversationReadTracker for why the
  // list can't just wait for it.
  const readTrackerRef = useRef(createConversationReadTracker());
  // Generation counter so in-flight fetchConversations() calls can be invalidated
  // by newer ones. Prevents a stale concurrent fetch (fired e.g. on login) from
  // clobbering state committed by a newer fetch (e.g. after accepting an invite).
  const fetchConversationsGenRef = useRef(0);
  const [, setInitialMapZoom] = useState(15.5);
  const [, setShowExploreListings] = useState(false);
  const [, setShowFilters] = useState(false);
  const [showRemoveListingDialog, setShowRemoveListingDialog] = useState<
    string | null
  >(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isBottomNavMobile, setIsBottomNavMobile] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [backendVersion, setBackendVersion] = useState<string>('unknown');
  const [hasCheckedLogin, setHasCheckedLogin] = useState(false);
  // A completed login owns the session; a startup check begun earlier must not overwrite it.
  const loginStatusGenerationRef = useRef(0);
  // Default `true` to suppress the marketing consent modal until the backend
  // tells us otherwise — prevents modal flicker during the initial
  // /check-login-status request.
  const [marketingConsentShown, setMarketingConsentShown] =
    useState<boolean>(true);
  const modals = useModals();
  // Global bottom-nav (JAA-266): Tools-tab sheet open state. Messages uses
  // `messagesModalVisible`.
  const [toolsSheetOpen, setToolsSheetOpen] = useState(false);
  // Tools-card flow overlays (JAA-285). Rendered only while the sheet is open,
  // so closing the sheet (nav switch, logout) tears them down too.
  const [boostFlowOpen, setBoostFlowOpen] = useState(false);
  const [estimateFlowOpen, setEstimateFlowOpen] = useState(false);
  // Activity-tab view open state. The Activity tab opens the sectioned
  // Activity view (My places / buying interests / following).
  const [activityViewOpen, setActivityViewOpen] = useState(false);
  // Activity View data (JAA-284): the assembled sections + nav-badge total from
  // GET /activity. Null until the first fetch resolves (or on failure).
  const [activityData, setActivityData] = useState<ActivityResponse | null>(
    null,
  );
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  // Buying-interest success toast (JAA-315): the interest being celebrated and
  // its matching-listing count, null when no toast is up. The identity rides
  // along so a dismissal can tell whether it is retiring this toast or one a
  // newer interest has already replaced.
  const [interestToast, setInterestToast] = useState<{
    pending: PendingInterestToast;
    count: number;
  } | null>(null);
  // Deep-linked Activity row (the digest email CTA, /activities?interest=<id>):
  // handed to ActivityView, which opens that row's detail once the row shows up
  // in freshly fetched data and then consumes it.
  const [activityDeepLink, setActivityDeepLink] = useState<{
    section: ActivitySection;
    entityId: string;
  } | null>(null);
  const consumeActivityDeepLink = useCallback(
    () => setActivityDeepLink(null),
    [],
  );
  // Generation counter so a stale in-flight fetchActivityData() can't clobber a
  // newer one's result (mirrors fetchConversationsGenRef).
  const activityGenRef = useRef(0);
  // True while a full-screen mobile overlay owned by NewLandingPage (such as
  // address search, Filters, or the burger/More menu) is open. Combined with the
  // profile route to hide the bottom nav on mobile (<=1024px).
  const [landingOverlayOpen, setLandingOverlayOpen] = useState(false);
  // Slide direction for the mobile tab transition (set on each nav tap).
  const [navSlideDir, setNavSlideDir] = useState<NavSlideDir>('forward');
  // Could-buy / could-sell route-driven modals (migrated from legacy LandingPage
  // when it was removed). Opened by the /could-sell and /could-buy routes.
  const [showCouldSellModal, setShowCouldSellModal] = useState(false);
  const [showCouldBuyModal, setShowCouldBuyModal] = useState(false);
  const [, setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal] =
    useState<OffMarketToastMessage | null>(null);

  // --- Admin: conversation moderation (lightweight MVP) ---
  const [adminModerationVisible, setAdminModerationVisible] = useState(false);
  const [adminModerationError, setAdminModerationError] = useState<
    string | null
  >(null);
  const [adminModerationSuccess, setAdminModerationSuccess] = useState<
    string | null
  >(null);
  const [
    adminRecommendedPropertiesVisible,
    setAdminRecommendedPropertiesVisible,
  ] = useState(false);
  const [adminDisableUserVisible, setAdminDisableUserVisible] = useState(false);
  const previewMetaUpdaterRef = useRef<BuildingPreviewMetaUpdater | null>(null);
  const buildingLikeAnimationHandlerRef =
    useRef<BuildingLikeAnimationHandler | null>(null);
  const addMyPlaceLauncherRef = useRef<AddMyPlaceLauncher | null>(null);
  const mapFocusHandlerRef = useRef<MapFocusHandler | null>(null);
  const showMapHandlerRef = useRef<ShowMapHandler | null>(null);

  usePriceEstimationPolling(isLoggedIn);

  // Keep refs in sync so the polling interval and the read-marking callback can
  // read current values without being re-created. Synced after commit, and
  // declared above the selection effect below so that effect reads the
  // committed list rather than the previous render's.
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
    conversationsRef.current = conversations;
  }, [selectedConversation, conversations]);

  // Nav badge = the unread sum of the list the user is looking at, derived
  // rather than stored so the badge can never disagree with the rows.
  const newMessagesCount = useMemo(
    () => conversations.reduce((total, conv) => total + (conv.unread || 0), 0),
    [conversations],
  );

  // Zero a conversation's unread count on the spot, without waiting for the
  // server: opening a chat is the read event, GET /conversations only confirms
  // it later.
  const markConversationRead = useCallback((conversationId: string) => {
    const known = conversationsRef.current.find((c) => c.id === conversationId);
    const lastMessageAt = known?.lastMessage?.timestamp?.getTime?.();
    readTrackerRef.current.markRead(
      conversationId,
      typeof lastMessageAt === 'number' ? lastMessageAt : null,
    );
    setConversations((prev) =>
      prev.some((c) => c.id === conversationId && c.unread > 0)
        ? prev.map((c) => (c.id === conversationId ? { ...c, unread: 0 } : c))
        : prev,
    );
  }, []);

  // Driven off the selection rather than the click so deep links and in-app
  // jumps into a chat count as reads too.
  useEffect(() => {
    if (selectedConversation) markConversationRead(selectedConversation);
  }, [selectedConversation, markConversationRead]);

  const registerPreviewMetaUpdater = useCallback(
    (updater: BuildingPreviewMetaUpdater | null) => {
      previewMetaUpdaterRef.current = updater;
    },
    [],
  );

  const syncBuildingPreviewMeta = useCallback<BuildingPreviewMetaUpdater>(
    (buildingId, patch) => {
      previewMetaUpdaterRef.current?.(buildingId, patch);
    },
    [],
  );

  const registerBuildingLikeAnimationHandler = useCallback(
    (handler: BuildingLikeAnimationHandler | null) => {
      buildingLikeAnimationHandlerRef.current = handler;
    },
    [],
  );

  const handleBuildingLikeChange = useCallback(
    (building: BuildingWithConvertedAddresses, liked: boolean) => {
      if (!liked) return;
      buildingLikeAnimationHandlerRef.current?.(building);
    },
    [],
  );

  const registerAddMyPlaceLauncher = useCallback(
    (launcher: AddMyPlaceLauncher | null) => {
      addMyPlaceLauncherRef.current = launcher;
    },
    [],
  );

  // Building view "Sell here": open the AMP flow (owned by NewLandingPage)
  // seeded with the already-known building, or unseeded (null) at the address
  // step for a district-only building (JAA-417). Stable identity so
  // buildingProps doesn't churn the modal stack.
  const handleSellHere = useCallback((seed: AddMyPlaceSeed | null) => {
    addMyPlaceLauncherRef.current?.(seed);
  }, []);

  // Tools flows' sell entry (JAA-285): open the AMP flow unseeded (address step
  // first) instead of the legacy /could-sell interest modal.
  const handleOpenAddMyPlaceUnseeded = useCallback(() => {
    addMyPlaceLauncherRef.current?.(null);
  }, []);

  const registerMapFocusHandler = useCallback(
    (handler: MapFocusHandler | null) => {
      mapFocusHandlerRef.current = handler;
    },
    [],
  );

  const registerShowMapHandler = useCallback(
    (handler: ShowMapHandler | null) => {
      showMapHandlerRef.current = handler;
    },
    [],
  );

  // Building view "View map" (and the mobile back arrow): hand the building back
  // to the landing map, which owns the camera and the on-map preview. Stable
  // identity so buildingProps doesn't churn the modal stack.
  const handleReturnToMap = useCallback<MapFocusHandler>(
    (buildingId, coords) => {
      mapFocusHandlerRef.current?.(buildingId, coords);
    },
    [],
  );

  // The logo's global-home action: leave the building page entirely. The
  // URL-sync effect drops building-id/lat/lng once this state clears.
  const closeBuildingView = useCallback(() => {
    setShowBuildingModal(false);
    setSelectedBuilding(null);
  }, []);

  // Tears down every piece of account-scoped state the moment logout begins,
  // so no authenticated UI or polling survives into the logged-out session.
  const handleLogoutStart = useCallback(() => {
    // The profile route prompts a logged-out visitor to log in, and logging out
    // while standing on it satisfies that condition for the frame between the
    // state flipping and the reload landing: the auth modal flashed up. One
    // shot, cleared by the guard itself, so a later visit still prompts.
    setIsLoggingOut(true);
    // Immediately stop anything that depends on an authenticated session
    setIsLoggedIn(false);

    // Stop token refresh scheduling
    setExpiresAt(null);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    // Clear account-scoped state
    setUserEmail(null);
    setIsAdmin(false);

    // Close/clear authenticated UI + polling drivers
    setMessagesModalVisible(false);
    setSelectedConversation(null);
    setDraftChat(null);
    setConversations([]);
    readTrackerRef.current.clear();

    setShowBuildingModal(false);
    setSelectedBuilding(null);

    // Close the Tools sheet + Activity view (JAA-266) so they don't reappear
    // on next login
    setToolsSheetOpen(false);
    setBoostFlowOpen(false);
    setEstimateFlowOpen(false);
    setActivityViewOpen(false);

    // Close any open overlays
    setLoginModalVisible(false);
    setShowExploreListings(false);
    setShowFilters(false);
    setShowRemoveListingDialog(null);

    // Close admin moderation UI
    setAdminModerationVisible(false);
    setAdminModerationError(null);
    setAdminModerationSuccess(null);
    setAdminRecommendedPropertiesVisible(false);

    // Reset marketing consent modal state on logout so the next signup can
    // re-trigger it normally.
    modals.close('marketingConsent');
    setMarketingConsentShown(true);
    sessionStorage.removeItem('marketing_consent_modal_seen');
    sessionStorage.removeItem('marketing_consent_deferred');

    clearPendingDraftChat();
  }, []);

  /**
   * The logout request failed: the session is already torn down locally, so
   * put the view back in the caller's hands rather than leaving it spinning on
   * a reload that is never coming.
   */
  const handleLogoutFailed = useCallback(() => {
    setIsLoggingOut(false);
  }, []);

  const handleLogoutSuccess = useCallback(() => {
    // Hard reload is acceptable UX and ensures no leftover timers/state
    window.location.assign('/');
  }, []);

  // Ensure refreshToken is stable and doesn't change on every render
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const csrfToken = getCookie('csrf_refresh_token');
      if (!csrfToken) {
        console.error('CSRF token not found');
        setIsLoggedIn(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/refresh-token`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setIsLoggedIn(true);
        const newExpiresAt = Date.now() + data.expires_in * 1000;
        setExpiresAt(newExpiresAt); // Update expiresAt to schedule next refresh
      } else {
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      setIsLoggedIn(false);
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Effect to schedule token refresh when expiresAt changes
  useEffect(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (expiresAt != null) {
      const minRefreshInterval = 60 * 1000; // 60 seconds in milliseconds
      const timeUntilExpiry = expiresAt - Date.now();

      if (timeUntilExpiry > minRefreshInterval) {
        let refreshTime = timeUntilExpiry - minRefreshInterval;

        // Cap the timeout to the maximum 32-bit signed integer to prevent immediate execution loop
        // due to integer overflow (approx 24.8 days) when tokens have long expiration times (e.g. 30 days).
        const MAX_TIMEOUT = 2147483647;
        if (refreshTime > MAX_TIMEOUT) {
          refreshTime = MAX_TIMEOUT;
        }

        refreshTimeoutRef.current = setTimeout(() => {
          refreshToken();
        }, refreshTime);
      } else if (timeUntilExpiry > 0) {
        console.warn('Token expires soon; refreshing token immediately.');
        refreshToken();
      } else {
        console.warn('Token has expired; user will be logged out.');
        setIsLoggedIn(false);
      }
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [expiresAt, refreshToken]); // Only depends on expiresAt

  // Safety: if we become logged out, ensure refresh scheduling is fully cleared
  useEffect(() => {
    if (!isLoggedIn) {
      setExpiresAt(null);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const checkLoginStatus = async () => {
      const requestGeneration = loginStatusGenerationRef.current;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/check-login-status`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-CSRF-TOKEN': getCookie('csrf_access_token'),
            },
          },
        );
        const data = await response.json();
        if (requestGeneration !== loginStatusGenerationRef.current) return;
        // Coerce: a 401 body carries no isLoggedIn key, which would put
        // `undefined` into state that is typed boolean and silently break every
        // `isLoggedIn === false` comparison downstream (JAA-325).
        setIsLoggedIn(!!data.isLoggedIn);
        if (data.isLoggedIn && data.expires_in) {
          setUserEmail(data.email);
          setIsAdmin(data.is_admin);
          setBackendVersion(data.backend_version ?? 'unknown');
          // Grandfather: if the backend omits the field (older response shape),
          // default to true so we never show the modal to a logged-in user we
          // can't classify.
          setMarketingConsentShown(data.marketing_consent_shown ?? true);
          const newExpiresAt = Date.now() + data.expires_in * 1000;
          setExpiresAt(newExpiresAt); // Update expiresAt to schedule next refresh

          TagManager.dataLayer({
            event: 'login_status',
            email: data.email,
            isLoggedIn: true,
          });
        } else {
          TagManager.dataLayer({
            event: 'login_status',
            isLoggedIn: false,
          });
        }
      } catch (error) {
        if (requestGeneration !== loginStatusGenerationRef.current) return;
        console.error('Error checking login status:', error);
        setIsLoggedIn(false);
      } finally {
        if (requestGeneration === loginStatusGenerationRef.current) {
          setHasCheckedLogin(true);
        }
      }
    };

    checkLoginStatus();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Draft-chat opener (JAA-275, revised per review): dispatched by the
  // Could-buy call sites (NewLandingPage cards, building-page listings) via a
  // window event — same pattern as 'jaakko:listings-changed' — so deep trees
  // need no new prop plumbing. Opens Messages on an ephemeral draft chat. Used
  // on a listing opened from the chat, it goes back to the chat (JAA-445). The
  // draft it drops or replaces takes its parked Send with it, so a later login
  // never sends the text of a draft that is gone (JAA-447).
  useEffect(() => {
    const onOpenDraftChat = (e: Event) => {
      const detail = (e as CustomEvent<DraftChatContext>).detail;
      if (!detail?.sellWillingnessId) return;
      clearPendingDraftChat();
      // The callers no longer POST anything, so they cannot tell whether a chat
      // with this seller already exists — that answer lives here, in the list.
      // Without it, a buyer returning to a listing they have already written to
      // would be dropped into an empty draft instead of their history.
      const existing = conversations.find(
        (c) => c.offMarketSellWillingnessId === detail.sellWillingnessId,
      );
      if (existing) {
        setDraftChat(null);
        setSelectedConversation(existing.id);
      } else {
        setDraftChat(detail);
        setSelectedConversation(null);
      }
      if (messagesModalVisibleRef.current) {
        returningToChatRef.current = stepBackToChat();
      }
      rememberPathBeforeMessages();
      setMessagesModalVisible(true);
    };
    window.addEventListener('jaakko:open-draft-chat', onOpenDraftChat);
    return () =>
      window.removeEventListener('jaakko:open-draft-chat', onOpenDraftChat);
  }, [conversations, stepBackToChat]);

  // JAA-355: "Open chat" on a listing card carries the conversation id the
  // backend annotated, so this opens it directly — the invite-accept pattern.
  // Deliberately NOT resolved against the `conversations` state list: that
  // list is only fetched when the Messages modal opens, so it is empty on the
  // first click; MessagesModal handles a selected id resolving as the list
  // arrives. Ignored while logged out (token expiry, logout in flight): a
  // retained card can still carry a badge, but fetchConversations bails for
  // logged-out viewers, so the modal would open empty and broken. Like the draft
  // opener, on a listing opened from the chat it goes back to the chat (JAA-445).
  useEffect(() => {
    const onOpenConversation = (e: Event) => {
      const conversationId = (e as CustomEvent<{ conversationId?: string }>)
        .detail?.conversationId;
      if (!conversationId || !isLoggedIn) return;
      setDraftChat(null);
      fetchConversations();
      setSelectedConversation(conversationId);
      if (messagesModalVisibleRef.current) {
        returningToChatRef.current = stepBackToChat();
      }
      rememberPathBeforeMessages();
      setMessagesModalVisible(true);
    };
    window.addEventListener('jaakko:open-conversation', onOpenConversation);
    return () =>
      window.removeEventListener(
        'jaakko:open-conversation',
        onOpenConversation,
      );
  }, [isLoggedIn, stepBackToChat]);


  /**
   * Reopen a chat the user was writing before they authenticated (JAA-325).
   * Runs once the login check has resolved, and covers the two cases that
   * destroy React state: an iOS Google popup tab that navigated itself to the
   * app root, and the new-deploy reload in index.tsx. The parked copy lives in
   * this tab's sessionStorage; a signed-out fresh visit discards it instead (see
   * pendingDraftChatToRestore). A signed-out load also drops the consent
   * deferral a torn-down login modal left behind, so a later signup in this tab
   * still gets the consent prompt. Skipped when the user already opened a gate
   * or a draft before the login check resolved. Runs again after a login, for a
   * tab that booted with the login modal open (a failed popup login retried).
   * Restores the text; MessagesModal then sends it once if no tab has claimed
   * that parked Send yet (JAA-447).
   */
  useEffect(() => {
    if (!hasCheckedLogin) return;
    // A gate opened while the login check was in flight owns the deferral and any new draft; leave both alone.
    if (loginModalVisible || draftChat) return;
    if (!isLoggedIn) {
      try {
        sessionStorage.removeItem('marketing_consent_deferred');
      } catch {
        // Storage blocked: nothing was deferred in this tab.
      }
    }
    const pending = pendingDraftChatToRestore(isLoggedIn);
    if (!pending) return;
    setDraftChat({
      sellWillingnessId: pending.sellWillingnessId,
      title: pending.title,
      initialText: pending.text,
    });
    setSelectedConversation(null);
    rememberPathBeforeMessages();
    setMessagesModalVisible(true);
    // When the login check resolves (isLoggedIn is set in that same batch) and again on a login or logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCheckedLogin, isLoggedIn]);

  // A failed redirect-flow Google login (iOS popup tab navigates back here) lands on /?login_error=<reason>; open the modal so SignupLoginModal can surface it.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('login_error')) {
      setLoginModalVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleShowBuildingModal = useCallback(() => {
    setShowBuildingModal((prev) => !prev);
  }, []);

  const selectBuilding = useCallback(
    (building) => {
      // Don't recenter the map on selection — user's camera context stays
      // stable. Search input still drives mapCenter via its own path.
      setSelectedBuilding(building);
      toggleShowBuildingModal();

      // Enrich with the full stored doc (geometry, etc.) — the SAME source the
      // URL-open (?building-id) uses. Opening from a low-zoom map preview carries
      // no footprint polygon; fetching the stored building gives the hero its
      // real outline. Instant paint from `building`, then swap in the full doc.
      const id = building?.id;
      if (!id) return;
      fetch(
        `${process.env.REACT_APP_API_URL}/building-page?building-id=${id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
        },
      )
        .then((r) => r.json())
        .then((data) => {
          // Only apply if the user is still viewing this same building.
          if (data?.data?.id === id) {
            setSelectedBuilding((prev) => {
              if (prev?.id !== id) return prev;
              // Keep the LIVE click data — what the user actually pointed at —
              // over the stored doc, which can be stale or describe a DIFFERENT
              // building (wrong address/polygon). Mapbox is the source of truth;
              // the stored doc only supplies listings/counts/etc. The hero centres
              // on `centroid`, so a live click point keeps it on the right
              // building even when there's no captured footprint.
              return mergeStoredBuilding(prev, data.data);
            });
          }
        })
        .catch(() => {});
    },
    [setSelectedBuilding, toggleShowBuildingModal],
  );

  // Modify handleLoginSuccess to accept expires_in and set expiresAt.
  // The second arg threads the marketing_consent_shown value from the auth
  // response so the post-signup modal trigger has the correct state without
  // requiring an extra /check-login-status round-trip. It also asks every open
  // list to refetch, because rows fetched while signed out cannot mark the
  // viewer's own listings and interests.
  const handleLoginSuccess = useCallback(
    (expiresIn: number, marketingConsentShownValue: boolean) => {
      loginStatusGenerationRef.current += 1;
      // Authentication settled the session even if the startup request is still pending.
      setHasCheckedLogin(true);
      setIsLoggedIn(true);
      setMarketingConsentShown(marketingConsentShownValue);
      const newExpiresAt = Date.now() + expiresIn * 1000;
      setExpiresAt(newExpiresAt); // Update expiresAt to schedule next refresh
      window.dispatchEvent(new CustomEvent('jaakko:listings-changed'));
    },
    [],
  );

  // When a logged-out user lands on /profile (e.g. via direct URL or after
  // logout), prompt them to log in and remember the path so we can return
  // them there once authenticated.
  useEffect(() => {
    if (!profileNav.isProfileRoute) {
      setProfileReturnPathAfterLogin(null);
      return;
    }
    if (!hasCheckedLogin) return;
    if (!isLoggedIn) {
      if (isLoggingOut) return;
      setLoginModalVisible(true);
      setProfileReturnPathAfterLogin(`${location.pathname}${location.search}`);
    }
  }, [
    profileNav.isProfileRoute,
    hasCheckedLogin,
    isLoggedIn,
    isLoggingOut,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    if (!isLoggedIn || !profileReturnPathAfterLogin) return;
    if (
      `${location.pathname}${location.search}` !== profileReturnPathAfterLogin
    ) {
      history.replace(profileReturnPathAfterLogin);
    }
    setProfileReturnPathAfterLogin(null);
  }, [
    history,
    isLoggedIn,
    location.pathname,
    location.search,
    profileReturnPathAfterLogin,
  ]);

  // Schedule the post-signup marketing consent modal. The deferred timer
  // makes the modal feel like part of natural onboarding (per JAA-68 product
  // feedback) rather than an aggressive interrupt.
  //
  // CRITICAL: the cleanup function MUST clearTimeout. Otherwise a fast user
  // who submits the consent within the delay window would still see the
  // modal flash open again when the orphaned timer fires.
  useEffect(() => {
    if (!isLoggedIn || marketingConsentShown) return;
    // Defensive guard: prevent re-show in this session even if state desyncs.
    if (sessionStorage.getItem('marketing_consent_modal_seen') === '1') return;
    // JAA-226: when login was triggered from a buy/sell Confirm, defer
    // marketing consent for the rest of this browser session so the auto-
    // resumed intent → SharePromptModal → DMs chain isn't interrupted. The
    // backend keeps marketing_consent_shown=false until the user actually
    // answers, so the modal naturally re-surfaces on the next session.
    if (sessionStorage.getItem('marketing_consent_deferred') === '1') return;
    const id = setTimeout(() => {
      sessionStorage.setItem('marketing_consent_modal_seen', '1');
      modals.open('marketingConsent', {
        onClose: () => {
          modals.close('marketingConsent');
          setMarketingConsentShown(true);
        },
      });
    }, MARKETING_CONSENT_MODAL_DELAY_MS);
    return () => clearTimeout(id);
  }, [isLoggedIn, marketingConsentShown, modals]);

  const [sharedEventId, setSharedEventId] = useState<string | null>(null);

  // Last route the deep-link effect saw, so a re-run caused only by the login
  // state can be told apart from navigation (JAA-325).
  const lastDmRouteRef = useRef(location.pathname + location.search);

  // --- Deep link handling: /direct-messages with ?conversation-id= or ?invite= ---
  useEffect(() => {
    const pathname = location.pathname;
    const params = new URLSearchParams(location.search);
    const prevRoute = lastDmRouteRef.current;
    const routeChanged = prevRoute !== pathname + location.search;
    lastDmRouteRef.current = pathname + location.search;
    const convId = params.get('conversation-id');
    const inviteId = params.get('invite');

    if (pathname === '/direct-messages') {
      // Keep the building view mounted underneath: both 'building' and 'dms'
      // live on the ModalOutlet stack, so the later-opened Messages modal
      // layers above it and closing Messages returns to the building page
      // (JAA-275: "Could buy" on a building-page card opens the request in
      // place).
      setMessagesModalVisible(true);
      if (!hasCheckedLogin) return;

      if (!isLoggedIn) {
        if ((convId || inviteId) && !loginModalVisible)
          setLoginModalVisible(true);
        if (selectedConversation) setSelectedConversation(null);
        return;
      }

      // Invite takes priority — accepting creates a conversation and redirects
      if (inviteId) {
        const processInvite = async () => {
          try {
            const data = await apiRequest(`/invite/accept/${inviteId}`, {
              method: 'POST',
            });
            const url = new URL(window.location.href);
            url.searchParams.delete('invite');
            url.pathname = '/direct-messages';
            url.searchParams.set('conversation-id', data.conversationId);
            history.replace(url.pathname + url.search);
            fetchConversations();
            setSelectedConversation(data.conversationId);
            setMessagesModalVisible(true);
          } catch (error: any) {
            console.error('Error accepting invite:', error);
            const url = new URL(window.location.href);
            url.searchParams.delete('invite');
            history.replace(url.pathname + url.search);
            alert(error?.error || 'This invite is no longer valid.');
          }
        };
        processInvite();
        return;
      }

      // Stepping Back from a listing to Messages: the chat button chose its own conversation or draft, so keep it over the one this URL remembers (JAA-445).
      if (returningToChatRef.current) {
        history.replace(withConversationParam(selectedConversation));
      } else if (convId) {
        setSelectedConversation(convId);
      }
    } else {
      // Profile and the single-listing view are top layers over the current nav
      // view. If the user opens either from Messages, keep Messages mounted
      // underneath just like Tools does. A draft chat is stacked without a URL
      // too: keep it when this run came from a login-state change, so an
      // in-page login never drops the message being sent (JAA-325), while real
      // navigation still closes it as before. A route change while the auth
      // modal is open over a draft is Back pressed at the Send gate: it cancels
      // the login instead, leaves the draft and its text on screen and drops the
      // parked copy, so a later login never sends it (JAA-447). Back off a
      // listing or Profile opened over Messages, onto the page Messages was
      // opened over, keeps it too: that is where Messages lives (JAA-445).
      const backFromSendGate = !!draftChat && routeChanged && loginModalVisible;
      if (backFromSendGate) {
        setLoginModalVisible(false);
        clearPendingDraftChat();
        try {
          sessionStorage.removeItem('marketing_consent_deferred');
        } catch {
          // Storage blocked: nothing was deferred in this tab.
        }
      }
      const [hostPath, hostSearch = ''] = prevPathRef.current.split('?');
      const backToMessagesHost =
        routeChanged &&
        history.action === 'POP' &&
        (prevRoute.startsWith('/listing/') || prevRoute.startsWith('/profile')) &&
        hostPath === pathname &&
        new URLSearchParams(hostSearch).get('building-id') ===
          params.get('building-id');
      const preserveMessagesUnderTopLayer =
        (profileNav.isProfileRoute ||
          isListingRoute ||
          backToMessagesHost ||
          (!!draftChat && (!routeChanged || backFromSendGate))) &&
        messagesModalVisible;

      if (!preserveMessagesUnderTopLayer) {
        // Leaving the DMs path should close modal and clear selection.
        if (messagesModalVisible) setMessagesModalVisible(false);
        if (selectedConversation) setSelectedConversation(null);
        if (draftChat) {
          setDraftChat(null);
          clearPendingDraftChat();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, isLoggedIn, hasCheckedLogin]);

  /**
   * Keep stepping Back after a chat button on a listing opened from the chat,
   * one listing per history change, until the chat's own page is on screen
   * (JAA-445). Stops as soon as Messages closes. Declared after the route
   * effect, which reads the flag on the pass that lands on Messages.
   */
  useEffect(() => {
    if (!returningToChatRef.current) return;
    returningToChatRef.current = messagesModalVisible && stepBackToChat();
  }, [location, messagesModalVisible, stepBackToChat]);

  /**
   * Track whether Profile sits between the open chat and the listing on screen
   * (Messages, then Profile, then a listing opened from it): set on a Profile
   * route over the chat, cleared back on the chat's own page or when Messages
   * closes (JAA-445).
   */
  useEffect(() => {
    if (!messagesModalVisible) {
      profileOverChatRef.current = false;
    } else if (profileNav.isProfileRoute) {
      profileOverChatRef.current = true;
    } else if (!isListingRoute) {
      profileOverChatRef.current = false;
    }
  }, [messagesModalVisible, profileNav.isProfileRoute, isListingRoute]);

  useEffect(() => {
    // Check for 'building-id' and 'event-id' in the URL first
    const params = new URLSearchParams(window.location.search);
    const buildingId = params.get('building-id');
    const eventId = params.get('event-id');

    // Store event ID for later use
    if (eventId) {
      setSharedEventId(eventId);
    }

    // Set a timeout to ensure we don't show loading screen forever
    const locationTimeout = setTimeout(() => {
      if (!isLocationLoaded) {
        console.warn(
          'Location determination timed out, using default location',
        );
        setIsLocationLoaded(true);
      }
    }, 10000); // 10 seconds timeout

    if (buildingId) {
      // Cold deep-link: derive a coordinate seed (URL coords -> a listing's
      // coords -> the id->centroid index), open the building, and let the hero
      // resolve the footprint by id from Mapbox. The address comes from a
      // reverse-geocode of the clicked point: the geocoder's first address or,
      // with none, its district (JAA-417); BuildingView still prefers a
      // listing's canonical address when one exists. building-data is no longer
      // the source here — it only covered ~13% of buildings.
      const urlLat = params.get('lat');
      const urlLng = params.get('lng');
      resolveBuildingSeed(buildingId, urlLat, urlLng)
        .then((seed) => {
          if (!seed) {
            // Never-seen, listing-less building on a bare link: no coordinate
            // exists anywhere, so it cannot be located. Degrade gracefully.
            performNormalLocationFlow();
            return;
          }
          const seedBuilding = {
            id: seed.id,
            centroid: { lat: seed.lat, lng: seed.lng },
            addresses: [],
            geometry: { type: 'Polygon', coordinates: [] },
            properties: { name: '', addresses: [] },
            pois: [],
          } as unknown as BuildingWithConvertedAddresses;
          setColdBuildingDeepLinkOpen(true);
          setSelectedBuilding(seedBuilding);
          setMapCenter({ lat: seed.lat, lng: seed.lng });
          setShowBuildingModal(true);
          setIsLocationLoaded(true);
          clearTimeout(locationTimeout);
          setInitialMapZoom(15.5);
          // Footprint resolves client-side: the hero map reads it from its own
          // rendered `building` layer and shares it with useSideAddresses — no
          // /building-geometry backend call.
          // Reverse-geocode the seed point for the address + POIs. The helper
          // works on a copy, so swap it in only while the seed is still shown.
          geocodeColdSeed(seedBuilding, (enriched) => {
            setSelectedBuilding((prev) => (prev === seedBuilding ? enriched : prev));
            const country = enriched.addresses?.[0]?.country;
            if (country) setCountryOfFeedContent(country);
          });
        })
        .catch(() => performNormalLocationFlow());
    } else {
      // Normal flow when there's no building ID
      performNormalLocationFlow();
    }

    function performNormalLocationFlow() {
      getUserCountryWithCoordinates()
        .then(({ country, coordinates }) => {
          setCountryOfFeedContent(country);
          // When GPS is denied, center the map on the country coordinates from IP
          if (coordinates) {
            setMapCenter(coordinates);
            setInitialMapZoom(15.5);
          }
          setIsLocationLoaded(true);
          clearTimeout(locationTimeout);
        })
        .catch((err) => {
          console.error('Error getting country from IP:', err);
          // If even IP location fails, just show the map with defaults
          setIsLocationLoaded(true);
          clearTimeout(locationTimeout);
        });
    }

    return () => {
      clearTimeout(locationTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedBuilding || !showBuildingModal) {
      setColdBuildingDeepLinkOpen(false);
    }
  }, [selectedBuilding, showBuildingModal]);

  // Update the URL when the building modal visibility or selected building changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedBuilding && showBuildingModal) {
      // Add 'building-id' (+ the building's point) to the URL so a refresh or a
      // shared link can re-seed Mapbox — the feature id alone is not geo-locatable.
      url.searchParams.set('building-id', selectedBuilding.id);
      const c = selectedBuilding.centroid;
      if (c && Number.isFinite(c.lat) && Number.isFinite(c.lng)) {
        url.searchParams.set('lat', String(c.lat));
        url.searchParams.set('lng', String(c.lng));
      }
      window.history.pushState({}, '', url.toString());
    } else {
      // Remove 'building-id' (and its point) from the URL
      url.searchParams.delete('building-id');
      url.searchParams.delete('lat');
      url.searchParams.delete('lng');
      window.history.pushState({}, '', url.toString());
    }
  }, [selectedBuilding, showBuildingModal]);

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Keep this aligned with Navigation.scss's mobile breakpoint (<=1024px).
  // The existing `isMobile` state is narrower and drives unrelated flows.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    const syncBottomNavMobile = () => {
      setIsBottomNavMobile(mediaQuery.matches);
    };

    syncBottomNavMobile();
    mediaQuery.addEventListener('change', syncBottomNavMobile);

    return () => {
      mediaQuery.removeEventListener('change', syncBottomNavMobile);
    };
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!isLoggedIn) return;

    // Bump the generation counter and remember this call's gen so we can bail
    // later if a newer fetchConversations() supersedes us. Without this guard,
    // a stale in-flight fetch (e.g. the one fired by the isLoggedIn effect at
    // mount) can clobber the state committed by a newer fetch (e.g. the one
    // fired after processInvite accepts an invite), and the cleanup block below
    // would then strip a valid `conversation-id` from the URL because the stale
    // list doesn't yet contain the newly-joined conversation.
    const gen = ++fetchConversationsGenRef.current;
    setIsLoadingConversations(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/conversations`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }

      const data = await response.json();

      // A newer fetchConversations() has been fired since we started. Drop this
      // result on the floor — including the cleanup check — so we don't clobber
      // state with stale data.
      if (gen !== fetchConversationsGenRef.current) return;

      // Transform the data to match your component's expected format
      const formattedConversations = data.map((conv: any) => {
        // Validate timestamps before creating Date objects
        let lastMessageTimestamp;
        try {
          lastMessageTimestamp = new Date(conv.lastMessage.timestamp);
          if (isNaN(lastMessageTimestamp.getTime())) {
            console.warn(
              'Invalid lastMessage timestamp in conversation:',
              conv,
            );
            lastMessageTimestamp = new Date();
          }
        } catch (error) {
          console.error('Error parsing lastMessage timestamp:', error, conv);
          lastMessageTimestamp = new Date();
        }

        let endedAt;
        if (conv.endedAt) {
          try {
            endedAt = new Date(conv.endedAt);
            if (isNaN(endedAt.getTime())) {
              console.warn('Invalid endedAt timestamp in conversation:', conv);
              endedAt = undefined;
            }
          } catch (error) {
            console.error('Error parsing endedAt timestamp:', error, conv);
            endedAt = undefined;
          }
        }

        return {
          id: conv.id,
          otherUser: {
            name: conv.otherUser.name,
            avatar: conv.otherUser.avatar,
            verified: !!conv.otherUser.verified,
          },
          lastMessage: {
            text: conv.lastMessage.text,
            timestamp: lastMessageTimestamp,
            firstMessage: conv.lastMessage.firstMessage,
            attachments: conv.lastMessage.attachments,
          },
          buildingInfo: {
            name: conv.buildingInfo.name,
            address: conv.buildingInfo.address,
          },
          unread: conv.unread,
          isEnded: conv.isEnded || false,
          endedByUserId: conv.endedByUserId,
          endedAt: endedAt,
          otherUserHasSentMessage: conv.otherUserHasSentMessage || false,
          otherUserRole: conv.otherUserRole,
          offMarketSellWillingnessId: conv.offMarketSellWillingnessId,
          offMarketBuyWillingnessId: conv.offMarketBuyWillingnessId,
          currentUserSide: conv.currentUserSide ?? null,
          status: conv.status ?? 'accepted',
          requestedBy: conv.requestedBy ?? null,
          hasViewedWelcome: conv.hasViewedWelcome || false,
          isJaakkoConversation: conv.isJaakkoConversation || false,
          participants: conv.participants || [],
          currentUserRole: conv.currentUserRole || 'member',
          canInvite: conv.canInvite || false,
          pendingInviteEmail: conv.pendingInviteEmail || null,
        };
      });

      // Local reads outrank the counts that just arrived: a count still
      // reflecting the pre-read state belongs to a request that raced the open.
      setConversations(
        readTrackerRef.current.reconcile(
          formattedConversations,
          selectedConversationRef.current,
        ),
      );

      // If deep-linked conversation-id exists, validate it against fetched conversations
      const currentParams = new URLSearchParams(window.location.search);
      const convId = currentParams.get('conversation-id');
      if (window.location.pathname === '/direct-messages' && convId) {
        const exists = formattedConversations.some((c: any) => c.id === convId);
        if (!exists) {
          // Clear invalid id and show list view
          setSelectedConversation(null);
          const url = new URL(window.location.href);
          url.searchParams.delete('conversation-id');
          history.replace(url.pathname + url.search);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      // Only clear the loading flag if we are the most recent fetch. A stale
      // fetch that got superseded above shouldn't prematurely reset the UI.
      if (gen === fetchConversationsGenRef.current) {
        setIsLoadingConversations(false);
      }
    }
  }, [isLoggedIn, history]);

  // Fetch the full Activity payload (sections + total_new). Generation-guarded
  // like fetchConversations so a stale in-flight call can't overwrite a newer
  // result (e.g. a re-fetch fired while the first is still pending).
  const fetchActivityData =
    useCallback(async (): Promise<ActivityResponse | null> => {
      const gen = ++activityGenRef.current;
      setIsLoadingActivity(true);
      try {
        const data = await fetchActivity();
        if (gen !== activityGenRef.current) return null;
        setActivityData(data);
        return data;
      } catch (e) {
        if (gen === activityGenRef.current) setActivityData(null);
        return null;
      } finally {
        if (gen === activityGenRef.current) setIsLoadingActivity(false);
      }
    }, []);

  // (Re)load Activity whenever the view opens while logged in.
  useEffect(() => {
    if (isLoggedIn && activityViewOpen) fetchActivityData();
  }, [isLoggedIn, activityViewOpen, fetchActivityData]);

  // Any listing change — a new buy interest or sell listing, an edit, a delete,
  // from anywhere in the app — invalidates these sections. Refresh regardless of
  // whether the Activity view is open: the nav badge is always on screen, so
  // waiting for the 60 s summary poll would leave a visibly stale count, and the
  // view must already be correct the moment it opens.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    window.addEventListener('jaakko:listings-changed', fetchActivityData);
    return () =>
      window.removeEventListener('jaakko:listings-changed', fetchActivityData);
  }, [isLoggedIn, fetchActivityData]);

  // Resolve a pending interest into its matching-listing count and show the
  // toast. Returns false when the interest no longer has an Activity row (it
  // was deleted while pending), so the caller can drop the pending record.
  const showInterestToast = useCallback(
    async (pending: PendingInterestToast) => {
      const data = await fetchActivityData();
      if (!data) return true;
      const row = data.buying_interests.find((r) =>
        pending.kind === 'area'
          ? r.kind === 'area' && r.area_interest_id === pending.id
          : r.kind === 'building' && r.willingness_id === pending.id,
      );
      if (!row) return false;
      // Superseded while awaiting /activity: the newer interest's resolver owns
      // the screen now, so showing this count would be showing the wrong one.
      if (!isStillPending(pending)) return true;
      setInterestToast({ pending, count: row.listing_count });
      return true;
    },
    [fetchActivityData],
  );

  // Clearing is permanent: the toast has now been seen (auto-dismissed or
  // tapped), so drop the stored record too or it would reappear next launch.
  // Only the record this toast was raised for, though — a newer interest
  // published while it was on screen owns storage now, and clearing blindly
  // would swallow that one's toast.
  const dismissInterestToast = useCallback(() => {
    setInterestToast((shown) => {
      if (shown && isStillPending(shown.pending)) clearPendingInterestToast();
      return null;
    });
  }, []);

  // JAA-315: after the could-buy flow closes on a freshly created interest,
  // store it as pending and show the toast with its matching-listing count.
  // Pending is persisted rather than expired on a timer: the surface that
  // renders it (the bottom nav) is unmounted on the listing route, so a toast
  // created there must survive until the user is back somewhere it can
  // actually be seen — including a later app launch.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const onInterestCreated = async (e: Event) => {
      const detail = (
        e as CustomEvent<{ kind: 'area' | 'building'; id: string }>
      ).detail;
      if (!detail?.id) return;
      // The building page hides the nav, which is where the toast lives, and
      // the buyer is done with that building the moment the interest exists.
      // Closing it hands them back the map with the bubble already up. Both
      // states, as everywhere else this closes: NewLandingPage reads a
      // non-null selectedBuilding as "the building view is up" and keeps the
      // explore controls hidden, so clearing only the flag returns a map with
      // no chrome.
      setShowBuildingModal(false);
      setSelectedBuilding(null);
      const pending: PendingInterestToast = {
        kind: detail.kind,
        id: detail.id,
        user: userEmail,
      };
      writePendingInterestToast(pending);
      const stillLive = await showInterestToast(pending);
      if (!stillLive && isStillPending(pending)) clearPendingInterestToast();
    };
    window.addEventListener(
      'jaakko:buying-interest-created',
      onInterestCreated,
    );
    return () =>
      window.removeEventListener(
        'jaakko:buying-interest-created',
        onInterestCreated,
      );
  }, [isLoggedIn, userEmail, showInterestToast]);

  // Replay a toast the user never got to see — created behind the listing
  // route, or in a session they closed before returning to the map.
  //
  // Also the retry path: a resolution that fails because /activity was down
  // leaves the record stored and nothing on screen, so this re-runs whenever
  // Activity data next lands. It cannot loop — a resolved toast sets the count
  // and returns early above, and a resolved-but-deleted interest clears the
  // record — and a failed fetch sets activityData to the null it already held,
  // which React bails out of rather than re-rendering.
  useEffect(() => {
    if (!isLoggedIn || !userEmail || interestToast !== null) return;
    const pending = readPendingInterestToast(userEmail);
    if (!pending) return;
    let cancelled = false;
    showInterestToast(pending).then((stillLive) => {
      if (!cancelled && !stillLive && isStillPending(pending)) {
        clearPendingInterestToast();
      }
    });
    return () => {
      cancelled = true;
    };
    // interestToast is read as a guard only; re-running on its change would
    // re-raise the toast the moment it is dismissed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, userEmail, showInterestToast, activityData]);

  // Poll the cheap summary so the nav badge updates without opening Activity.
  // Merge only total_new into existing state so the section lists aren't clobbered.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const tick = () =>
      apiRequest('/activity/summary')
        .then((r: { total_new: number }) =>
          setActivityData((prev) =>
            prev
              ? { ...prev, total_new: r.total_new }
              : {
                  my_places: [],
                  buying_interests: [],
                  following: [],
                  total_new: r.total_new,
                },
          ),
        )
        .catch(() => undefined);
    const id = window.setInterval(tick, 60000);
    tick();
    return () => window.clearInterval(id);
  }, [isLoggedIn]);

  // Optimistically zero the tapped row's "new" count and recompute total_new so
  // the badge drops immediately; the next /activity/summary poll reconciles the
  // exact value.
  const clearRowLocally = useCallback(
    (
      prev: ActivityResponse | null,
      section: ActivitySection,
      entityId: string,
      remaining = 0,
    ): ActivityResponse | null => {
      if (!prev) return prev;
      const myPlaces =
        section === 'my_places'
          ? prev.my_places.map((row) =>
              row.building_id === entityId
                ? { ...row, new_buyer_count: 0 }
                : row,
            )
          : prev.my_places;
      const buyingInterests =
        section === 'buying_interests'
          ? prev.buying_interests.map((row) => {
              const id =
                row.kind === 'area' ? row.area_interest_id : row.willingness_id;
              return id === entityId ? { ...row, new_listing_count: remaining } : row;
            })
          : prev.buying_interests;
      const following =
        section === 'following'
          ? prev.following.map((row) =>
              row.building_id === entityId
                ? { ...row, new_count: 0, new_like_count: 0 }
                : row,
            )
          : prev.following;
      const total_new =
        myPlaces.reduce((acc, row) => acc + row.new_buyer_count, 0) +
        buyingInterests.reduce((acc, row) => acc + row.new_listing_count, 0) +
        following.reduce(
          (acc, row) => acc + row.new_count + row.new_like_count,
          0,
        );
      return {
        my_places: myPlaces,
        buying_interests: buyingInterests,
        following,
        total_new,
      };
    },
    [],
  );

  useEffect(() => {
    const onSeen = (event: Event) => {
      const { section, entityId, remaining } = (event as CustomEvent<{
        section: ActivitySection; entityId: string; remaining: number;
      }>).detail;
      setActivityData((prev) => clearRowLocally(prev, section, entityId, remaining));
    };
    window.addEventListener('jaakko:activity-seen', onSeen);
    return () => window.removeEventListener('jaakko:activity-seen', onSeen);
  }, [clearRowLocally]);

  // Toggle the messages modal, loading conversations on open and clearing the
  // selected one on close so it never reopens on a stale thread.
  const toggleMessagesModal = useCallback(() => {
    setMessagesModalVisible((prev) => {
      if (!prev) {
        fetchConversations();
      } else {
        setSelectedConversation(null);
        // Closing the modal discards any unsent draft chat — by design, a
        // Could-buy click without a message leaves nothing behind.
        setDraftChat(null);
        clearPendingDraftChat();
      }
      return !prev;
    });

    TagManager.dataLayer({
      dataLayer: {
        event: 'toggle_messages_modal',
        category: 'User Interaction',
        action: 'Click',
        label: messagesModalVisible
          ? 'Close Messages Modal'
          : 'Open Messages Modal',
      },
    });

    // Update URL when toggling the modal
    const url = new URL(window.location.href);
    if (!messagesModalVisible) {
      // Opening
      rememberPathBeforeMessages();
      url.pathname = '/direct-messages';
      if (selectedConversation) {
        url.searchParams.set('conversation-id', String(selectedConversation));
      } else {
        url.searchParams.delete('conversation-id');
      }
      history.push(url.pathname + url.search);
    } else {
      // Messages opened without a URL of its own (a draft or restored chat, an
      // "Open chat" card, or anything over a listing) closes without touching
      // the URL, so the page underneath stays exactly where it was.
      if (window.location.pathname !== '/direct-messages') return;

      // Closing → return to previous non-DM path (or "/" if none). With a
      // building view open underneath (JAA-275 stacking), its URL wins so the
      // user lands back on the building page they came from.
      let fallback = prevPathRef.current || '/';
      if (showBuildingModal && selectedBuilding) {
        const buildingUrl = new URL(window.location.origin + '/');
        buildingUrl.searchParams.set('building-id', selectedBuilding.id);
        const c = selectedBuilding.centroid;
        if (c && Number.isFinite(c.lat) && Number.isFinite(c.lng)) {
          buildingUrl.searchParams.set('lat', String(c.lat));
          buildingUrl.searchParams.set('lng', String(c.lng));
        }
        fallback = buildingUrl.pathname + buildingUrl.search;
      } else if (fallback.startsWith('/direct-messages')) {
        fallback = '/';
      }
      // Ensure DM query params are cleared when leaving
      const targetUrl = new URL(window.location.origin + fallback);
      targetUrl.searchParams.delete('conversation-id');
      history.push(targetUrl.pathname + targetUrl.search);
    }
  }, [
    messagesModalVisible,
    selectedConversation,
    history,
    fetchConversations,
    showBuildingModal,
    selectedBuilding,
  ]);

  // Fetch conversations when logged in.
  // When a conversation is selected, MessagesModal handles polling for both
  // messages + conversations on the same interval, so we skip here.
  useEffect(() => {
    if (isLoggedIn) {
      fetchConversations();

      const intervalId = setInterval(() => {
        if (!selectedConversationRef.current) {
          fetchConversations();
        }
      }, 30000);

      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, fetchConversations]);

  // Refresh the conversation list when something elsewhere in the app has just
  // created one. Posting a building "could buy" onto a building that already has
  // a seller opens conversations inside that POST, then routes straight to
  // /direct-messages — which alone triggers no fetch, so the match only appeared
  // when the 30s poll above happened to run.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const handler = () => fetchConversations();
    window.addEventListener('jaakko:conversations-changed', handler);
    return () =>
      window.removeEventListener('jaakko:conversations-changed', handler);
  }, [isLoggedIn, fetchConversations]);

  // Reflect selected conversation in URL while modal is open
  useEffect(() => {
    if (!messagesModalVisible) return;
    if (window.location.pathname !== '/direct-messages') return;
    if (!isLoggedIn) return;

    history.replace(withConversationParam(selectedConversation));
  }, [selectedConversation, messagesModalVisible, isLoggedIn, history]);

  const handleSelectBuildingFromExplore = useCallback(
    async (buildingId: string) => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'open_listing_from_browse_listings',
          category: 'User Interaction',
          action: 'Click',
          label: 'Open Listing From Browse Listings',
        },
      });
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/building-page?building-id=${buildingId}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': getCookie('csrf_access_token'),
            },
          },
        );
        const data = await response.json();
        if (data && data.data) {
          const building = data.data as BuildingWithConvertedAddresses;
          // The listing view paints the building view from a seed (id +
          // centroid + the listing's address) before this fetch lands. The
          // stored doc has no `addresses`/`centroid` of its own, so swapping it
          // in wholesale threw that geo context away and left Street View (and
          // anything else keyed on the address) with nothing to anchor on —
          // the same building opened from the map kept working because that
          // path merges. Merge here too whenever the seed is still on screen.
          setSelectedBuilding((prev) =>
            prev?.id === building.id
              ? mergeStoredBuilding(prev, building)
              : building,
          );
          setShowBuildingModal(true);
        }
      } catch (error) {
        console.error('Error fetching building from explore:', error);
      }
    },
    [],
  );

  // Open the Messages (dms) modal over the NEW landing page too (JAA-266).
  // The legacy LandingPage owns this sync on its routes; the shell owns it
  // whenever the new landing is shown (incl. /direct-messages, now routed to the
  // new landing) so tapping Messages in the global nav opens the modal over the
  // explore map instead of dropping the user onto the legacy route.
  const dmsProps = useMemo(
    () => ({
      isLandingPage: true,
      isVisible: messagesModalVisible,
      toggleMessagesModal,
      conversations,
      isLoadingConversations,
      selectedConversation,
      setSelectedConversation,
      refreshConversations: fetchConversations,
      isLoggedIn,
      isSignedOut: hasCheckedLogin && !isLoggedIn,
      onLoginClick: () => setLoginModalVisible(true),
      slideDir: navSlideDir,
      draftChat,
      onCloseDraftChat: () => {
        // The composer's back arrow lands here, not in toggleMessagesModal, so
        // without this an abandoned draft stays in storage and the chat pops
        // back open on the next visit (JAA-325).
        setDraftChat(null);
        clearPendingDraftChat();
      },
    }),
    [
      messagesModalVisible,
      toggleMessagesModal,
      conversations,
      isLoadingConversations,
      selectedConversation,
      fetchConversations,
      isLoggedIn,
      hasCheckedLogin,
      navSlideDir,
      draftChat,
    ],
  );

  useEffect(() => {
    if (messagesModalVisible) {
      modals.open('dms', dmsProps);
    } else {
      modals.close('dms');
    }
  }, [messagesModalVisible, dmsProps, modals]);

  /**
   * Messages and Tools are sibling nav tabs, so Messages opening by any route
   * (the nav, Back or Forward onto /direct-messages, a chat opener) closes
   * Tools; otherwise the desktop Tools card stays painted over Messages and
   * the nav keeps Tools lit (JAA-445). Activity is only covered instead
   * (JAA-443): chats open from its rows and return to them.
   */
  useEffect(() => {
    if (messagesModalVisible) setToolsSheetOpen(false);
  }, [messagesModalVisible]);

  // Route-driven could-sell / could-buy modals (migrated from legacy LandingPage).
  useEffect(() => {
    if (location.pathname === '/could-sell') {
      setShowCouldSellModal(true);
      setShowCouldBuyModal(false);
      const paymentStatus = new URLSearchParams(location.search).get('payment');
      if (paymentStatus === 'success' || paymentStatus === 'cancelled') {
        history.replace('/could-sell');
      }
    } else if (location.pathname === '/could-buy') {
      setShowCouldBuyModal(true);
      setShowCouldSellModal(false);
    } else {
      // Off both routes (e.g. tabbed away via Explore) → close both so the
      // modal doesn't linger and actionType can't resolve to a stale 'sell'.
      setShowCouldSellModal(false);
      setShowCouldBuyModal(false);
    }
  }, [location.pathname, location.search, history]);

  // Email deep link: /activities?interest=<id> (the area-interest digest CTA)
  // opens the Activity view on that buying interest's row detail. Runs once the
  // login check has settled; logged out it opens the login modal and leaves the
  // URL in place so completing login re-runs this and still lands on the view.
  // The URL is then replaced with '/', mirroring the /direct-messages deep link.
  useEffect(() => {
    if (location.pathname !== '/activities' || !hasCheckedLogin) return;
    if (!isLoggedIn) {
      setLoginModalVisible(true);
      return;
    }
    const interestId = new URLSearchParams(location.search).get('interest');
    setActivityDeepLink(
      interestId ? { section: 'buying_interests', entityId: interestId } : null,
    );
    setToolsSheetOpen(false);
    if (messagesModalVisible) toggleMessagesModal();
    setActivityViewOpen(true);
    history.replace('/');
  }, [
    location.pathname,
    location.search,
    hasCheckedLogin,
    isLoggedIn,
    messagesModalVisible,
    toggleMessagesModal,
    history,
  ]);

  // Drop an unconsumed deep link when the view closes (e.g. the interest was
  // deleted and its row never appeared), so a later manual visit to Activity
  // can't auto-open a stale detail.
  useEffect(() => {
    if (!activityViewOpen) setActivityDeepLink(null);
  }, [activityViewOpen]);

  // Drive the could-buy/could-sell modal through the ModalOutlet controller
  // (registered as 'landingCouldBuyOrCouldSell'), consistent with dms/building.
  useEffect(() => {
    if (showCouldSellModal || showCouldBuyModal) {
      modals.open('landingCouldBuyOrCouldSell', {
        isOpen: true,
        actionType: showCouldSellModal ? 'sell' : 'buy',
        isLoading: false,
        isLoggedIn,
        userEmail,
        setLoginModalVisible,
        setShowBuildingModalTriggeredFromCouldBuyOrCouldSellModal,
        onSelectBuilding: handleSelectBuildingFromExplore,
        countryOfFeedContent: countryOfFeedContent || 'Finland',
        fetchConversations,
        onClose: () => {
          setShowCouldSellModal(false);
          setShowCouldBuyModal(false);
          if (
            location.pathname === '/could-sell' ||
            location.pathname === '/could-buy'
          ) {
            history.push('/');
          }
        },
      });
    } else {
      modals.close('landingCouldBuyOrCouldSell');
    }
  }, [
    showCouldSellModal,
    showCouldBuyModal,
    isLoggedIn,
    userEmail,
    countryOfFeedContent,
    handleSelectBuildingFromExplore,
    fetchConversations,
    modals,
    history,
    location.pathname,
  ]);

  const openAdminModeration = useCallback(() => {
    if (!isLoggedIn || !isAdmin || isMobile) return;
    setAdminModerationVisible(true);
    setAdminModerationError(null);
    setAdminModerationSuccess(null);
  }, [isLoggedIn, isAdmin, isMobile]);

  const openAdminRecommendedProperties = useCallback(() => {
    if (!isLoggedIn || !isAdmin || isMobile) return;
    setAdminRecommendedPropertiesVisible(true);
  }, [isLoggedIn, isAdmin, isMobile]);

  const openAdminDisableUser = useCallback(() => {
    if (!isLoggedIn || !isAdmin || isMobile) return;
    setAdminDisableUserVisible(true);
  }, [isLoggedIn, isAdmin, isMobile]);

  // Show loading screen if location is not loaded yet
  /**
   * Render the building modal through the shared modal stack (ModalOutlet) so it
   * layers with other modals by open-order instead of a hardcoded z-index —
   * mirroring the from-map path. Sync it to showBuildingModal + selectedBuilding.
   */
  const buildingProps = useMemo(
    () => ({
      isLandingPage: true,
      isLoggedIn,
      userEmail: userEmail || undefined,
      selectedBuilding,
      showImageModal,
      countryOfFeedContent: countryOfFeedContent || 'Finland',
      messagesModalVisible,
      sharedEventId,
      setSharedEventId,
      toggleShowBuildingModal,
      setSelectedBuilding,
      setLoginModalVisible,
      refreshFeed: () => {},
      setShowImageModal,
      setCountryOfFeedContent,
      fetchConversations,
      onBuildingPreviewMetaChange: syncBuildingPreviewMeta,
      onBuildingLikeChange: handleBuildingLikeChange,
      onSellHere: handleSellHere,
      onReturnToMap: handleReturnToMap,
      isAdmin: isMobile ? false : isAdmin,
    }),
    [
      isLoggedIn,
      userEmail,
      selectedBuilding,
      showImageModal,
      countryOfFeedContent,
      messagesModalVisible,
      sharedEventId,
      setSharedEventId,
      toggleShowBuildingModal,
      setSelectedBuilding,
      setLoginModalVisible,
      setShowImageModal,
      setCountryOfFeedContent,
      fetchConversations,
      syncBuildingPreviewMeta,
      handleBuildingLikeChange,
      handleSellHere,
      handleReturnToMap,
      isMobile,
      isAdmin,
    ],
  );

  useEffect(() => {
    // /direct-messages counts too: Messages stacks above the building view
    // (JAA-275), so entering DMs must not unmount the building layer.
    const buildingHostRoute =
      showNewLandingPage || location.pathname === '/direct-messages';
    if (buildingHostRoute && selectedBuilding && showBuildingModal) {
      modals.open('building', buildingProps);
    } else {
      modals.close('building');
    }
  }, [
    showNewLandingPage,
    location.pathname,
    selectedBuilding,
    showBuildingModal,
    buildingProps,
    modals,
  ]);

  /**
   * Render the profile view through the modal stack too, so it layers above the
   * building (and any other modal) by open-order rather than a hardcoded z-index.
   * Synced to the /profile route.
   */
  const profileProps = useMemo(
    () => ({
      userEmail,
      isLoggedIn,
      setIsLoggedIn,
      onClose: () => {
        if (profileCloseHistoryDelta !== null) {
          history.go(profileCloseHistoryDelta);
          return;
        }
        history.push(messagesModalVisible ? '/direct-messages' : '/');
      },
      onLogoutStart: handleLogoutStart,
      onLogoutSuccess: handleLogoutSuccess,
      onLogoutFailed: handleLogoutFailed,
      headerVariant: 'newLandingPage' as const,
    }),
    [
      userEmail,
      isLoggedIn,
      setIsLoggedIn,
      history,
      messagesModalVisible,
      profileCloseHistoryDelta,
      handleLogoutStart,
      handleLogoutSuccess,
      handleLogoutFailed,
    ],
  );

  useEffect(() => {
    if (
      showNewLandingPage &&
      profileNav.isProfileRoute &&
      (isLoggedIn || isLoggingOut)
    ) {
      modals.open('profile', profileProps);
    } else {
      modals.close('profile');
    }
  }, [
    showNewLandingPage,
    profileNav.isProfileRoute,
    isLoggedIn,
    isLoggingOut,
    profileProps,
    modals,
  ]);

  /**
   * Render the shared single-listing view through the modal stack too (JAA-306),
   * so it reuses the header + layers like the building view. Synced to the
   * `/listing/:id` route; closing returns to the surface it was opened from.
   */
  const listingId = listingLayerLocation?.pathname.split('/')[2];
  // Whether the history entry the user is standing on was pushed by this app,
  // which is the only case where closing may pop it. A cold or shared load must
  // push instead — popping there walks the user out of the app (JAA-345). The
  // marker is carried by the entry itself, so it still answers for an entry the
  // user came back to rather than only for the newest navigation.
  const listingEnteredByPush = !!(
    listingLayerLocation?.state as ListingLocationState | undefined
  )?.pushedByApp;
  const listingProps = useMemo(
    () => ({
      listingId,
      isLoggedIn,
      hasCheckedLogin,
      setLoginModalVisible,
      // Popping, not pushing: pushing the previous path grew history instead of
      // shrinking it, so one Back press re-entered the listing just closed.
      onClose: () => {
        if (!listingEnteredByPush) {
          history.push('/');
          return;
        }
        history.goBack();
      },
      // Area interests are not tied to their stored anchor building. Return to
      // Explore's map at the interest's representative centre, and clear a
      // building view that may be stacked underneath when the interest was
      // opened from a building page.
      onViewMap: (center?: LatLng) => {
        closeBuildingView();
        if (showMapHandlerRef.current) {
          showMapHandlerRef.current(center);
          return;
        }
        history.push('/');
      },
      // Open the building view directly (the URL deep-link handler is mount-only,
      // so a same-instance push to /?building-id wouldn't fire it). The building
      // must already be open before the push, or the listing modal closes onto a
      // bare map — but waiting on the fetch to achieve that left the button dead
      // for a whole round trip. Paint from the listing's seed instead and let the
      // full document land behind it, the way a cold deep-link does. Without a
      // seed there is nothing to paint, so that case still waits.
      onViewBuilding: (
        bid: string,
        seed: BuildingWithConvertedAddresses | null,
      ) => {
        if (!seed) {
          handleSelectBuildingFromExplore(bid).then(() =>
            history.push(`/?building-id=${bid}`),
          );
          return;
        }
        setSelectedBuilding(seed);
        setShowBuildingModal(true);
        history.push(`/?building-id=${bid}`);
        handleSelectBuildingFromExplore(bid);
      },
    }),
    [
      listingId,
      isLoggedIn,
      hasCheckedLogin,
      setLoginModalVisible,
      history,
      listingEnteredByPush,
      closeBuildingView,
      handleSelectBuildingFromExplore,
    ],
  );

  useEffect(() => {
    if (showNewLandingPage && listingId) {
      modals.open('listing', listingProps);
    } else {
      modals.close('listing');
    }
  }, [showNewLandingPage, listingId, listingProps, modals]);


  const signupLoginProps = useMemo(
    () => ({
      toggleSignupLoginModalVisible: () => setLoginModalVisible(false),
      setIsLoggedIn,
      isLoggedIn,
      onLoginSuccess: handleLoginSuccess,
      isLandingPage: true,
      setIsAdmin,
      setResponseEmail: setUserEmail,
      onLogoutStart: handleLogoutStart,
      onLogoutSuccess: handleLogoutSuccess,
    }),
    [isLoggedIn, handleLoginSuccess, handleLogoutStart, handleLogoutSuccess],
  );

  // `loginModalVisible` stays the source of truth: roughly thirty components
  // prop-drill its setter to demand a login, and the flags that hide the bottom
  // nav read it during render, where the controller's isOpen() is not reactive.
  // The stack only decides where the modal is drawn.
  useEffect(() => {
    if (loginModalVisible) {
      modals.open('signupLogin', signupLoginProps);
    } else {
      modals.close('signupLogin');
    }
  }, [loginModalVisible, signupLoginProps, modals]);

  if (!isLocationLoaded) {
    return <MapLoadingScreen />;
  }

  // --- Global bottom navigation (JAA-266) ---------------------------------
  // The SPA switches views by React state, not URL, so the active tab is
  // derived from which overlay is open rather than the router.
  const navActiveTab = getActiveTab({
    toolsOpen: toolsSheetOpen,
    messagesOpen: messagesModalVisible,
    activityOpen: activityViewOpen,
  });

  // Switches the bottom nav to Explore: closes every other nav surface
  // (Tools, Activity, Messages) and returns the router to the map root.
  const handleNavigateExplore = () => {
    setNavSlideDir(
      NAV_TAB_ORDER.explore >= NAV_TAB_ORDER[navActiveTab] ? 'forward' : 'back',
    );
    setToolsSheetOpen(false);
    setActivityViewOpen(false);
    if (messagesModalVisible) {
      setMessagesModalVisible(false);
      setSelectedConversation(null);
      // Match toggleMessagesModal: a close discards any unsent draft composer.
      setDraftChat(null);
      clearPendingDraftChat();
    }
    if (location.pathname !== '/') history.push('/');
  };

  /**
   * Activity's "Explore the map" button. Goes through the map-bound handler,
   * which also collapses the listings sheet, preview and filters kept under nav
   * views, so the user lands on the map the label names. Falls back to the
   * plain Explore tab switch when the map shell has not registered a handler.
   */
  const handleActivityExploreMap = () => {
    if (showMapHandlerRef.current) {
      showMapHandlerRef.current();
      return;
    }
    handleNavigateExplore();
  };

  const handleNavSelect = (tab: NavTabId) => {
    if (tab === navActiveTab) return;

    setNavSlideDir(
      NAV_TAB_ORDER[tab] >= NAV_TAB_ORDER[navActiveTab] ? 'forward' : 'back',
    );
    switch (tab) {
      case 'explore':
        handleNavigateExplore();
        break;
      case 'activity':
        setToolsSheetOpen(false);
        if (messagesModalVisible) toggleMessagesModal();
        if (location.pathname !== '/') history.push('/');
        setActivityViewOpen(true);
        break;
      case 'messages':
        setToolsSheetOpen(false);
        setActivityViewOpen(false);
        toggleMessagesModal();
        break;
      case 'tools':
        setActivityViewOpen(false);
        if (messagesModalVisible) toggleMessagesModal();
        if (location.pathname !== '/') history.push('/');
        setToolsSheetOpen((prev) => !prev);
        break;
    }
  };

  // Authenticated-only; globally hidden during flows that fully own the screen.
  // Message conversations are mobile/tablet-only hides below so the desktop
  // floating nav can keep its existing modal-clearance behavior.
  const navVisible =
    hasCheckedLogin &&
    isLoggedIn &&
    !showBuildingModal &&
    !listingId &&
    !loginModalVisible;

  // Hide the bottom nav on mobile (<=1024px, enforced in Navigation.scss) when a
  // full-screen overlay view is open: address search, Filters, the burger/More
  // menu (all via landingOverlayOpen), the Profile route, or an open Messages
  // conversation.
  // The Messages list keeps the nav; the conversation detail hides it so the
  // composer remains usable. A draft chat counts as open too: it is a full
  // conversation on screen but has no id yet, so reading selectedConversation
  // alone leaves the nav sitting on its composer. The listing layer hides the
  // nav at every breakpoint, even when Profile is open above the listing.
  const messageConversationOpen =
    messagesModalVisible && Boolean(selectedConversation || draftChat);
  const hideNavForMobileViews =
    landingOverlayOpen || profileNav.isProfileRoute || messageConversationOpen;
  const shouldRenderBottomNav =
    navVisible && !(isBottomNavMobile && landingOverlayOpen);
  const desktopNavModalBackdropVisible =
    shouldRenderBottomNav &&
    !isBottomNavMobile &&
    (messagesModalVisible || toolsSheetOpen || activityViewOpen);

  /**
   * A click on the shared nav backdrop closes the card the nav shows as active,
   * when that is Tools or Activity. Messages outranks Activity in getActiveTab,
   * so an Activity view covered by Messages is left alone rather than closed by
   * a click it was never seen to receive (JAA-443).
   */
  const handleDesktopNavBackdropClick = () => {
    if (navActiveTab === 'tools') setToolsSheetOpen(false);
    if (navActiveTab === 'activity') setActivityViewOpen(false);
  };

  return (
    <div
      className={`app-content-wrap${shouldRenderBottomNav ? ' has-bottom-nav' : ''}${hideNavForMobileViews ? ' app-content-wrap--hide-nav-mobile' : ''}`}
    >
      <ModalOutlet />
      {desktopNavModalBackdropVisible && (
        <div
          aria-hidden="true"
          className="app-content-wrap__desktop-nav-backdrop"
          onClick={handleDesktopNavBackdropClick}
        />
      )}
      <PageMetaTags
        title={t('metaTags.title')}
        description={t('metaTags.description')}
        ogTitle={t('metaTags.ogTitle')}
        ogDescription={t('metaTags.ogDescription')}
        ogImage="https://www.jaakko.com/jaakko_logo.png"
        ogUrl="https://www.jaakko.com"
      />

      {isLoggedIn && isAdmin && !isMobile && (
        <div
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <Button variant="warning" onClick={openAdminRecommendedProperties}>
            Admin: Recommended properties
          </Button>
          <Button variant="warning" onClick={openAdminModeration}>
            Admin: Moderate conversations
          </Button>
          <Button variant="warning" onClick={openAdminDisableUser}>
            Admin: Disable users
          </Button>
          <div
            style={{
              fontSize: 11,
              color: '#999',
              textAlign: 'right',
              marginTop: 4,
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            }}
            title="Jaakko release version (admin only). See RELEASING.md."
          >
            FE v{process.env.REACT_APP_VERSION} · BE v{backendVersion}
          </div>
        </div>
      )}

      <AdminRecommendedPropertiesTool
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        isMobile={isMobile}
        show={adminRecommendedPropertiesVisible}
        setShow={setAdminRecommendedPropertiesVisible}
      />

      <AdminDisableUserTool
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        isMobile={isMobile}
        show={adminDisableUserVisible}
        setShow={setAdminDisableUserVisible}
      />

      <AdminModerationTool
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        isMobile={isMobile}
        adminModerationVisible={adminModerationVisible}
        setAdminModerationVisible={setAdminModerationVisible}
        adminModerationError={adminModerationError}
        setAdminModerationError={setAdminModerationError}
        adminModerationSuccess={adminModerationSuccess}
        setAdminModerationSuccess={setAdminModerationSuccess}
        hasCheckedLogin={hasCheckedLogin}
      />

      <div className="app-content">
        <NewLandingPage
          selectedBuilding={selectedBuilding}
          selectBuilding={selectBuilding}
          userLocation={mapCenter}
          setLoginModalVisible={setLoginModalVisible}
          isLoggedIn={isLoggedIn}
          hasCheckedLogin={hasCheckedLogin}
          userEmail={userEmail}
          showRemoveListingDialog={showRemoveListingDialog}
          setShowRemoveListingDialog={setShowRemoveListingDialog}
          handleSelectBuildingFromExplore={handleSelectBuildingFromExplore}
          isAdmin={isMobile ? false : isAdmin}
          countryOfFeedContent={countryOfFeedContent || 'Finland'}
          setCountryOfFeedContent={setCountryOfFeedContent}
          navViewActive={navActiveTab !== 'explore'}
          onNavigateExplore={handleNavigateExplore}
          onOverlayVisibilityChange={setLandingOverlayOpen}
          onRegisterPreviewMetaUpdater={registerPreviewMetaUpdater}
          onRegisterBuildingLikeAnimationHandler={
            registerBuildingLikeAnimationHandler
          }
          onRegisterAddMyPlaceLauncher={registerAddMyPlaceLauncher}
          onRegisterMapFocusHandler={registerMapFocusHandler}
          onRegisterShowMapHandler={registerShowMapHandler}
          onCloseBuildingView={closeBuildingView}
          suspendMapWhileBuildingDeepLinked={
            coldBuildingDeepLinkOpen &&
            Boolean(selectedBuilding) &&
            showBuildingModal
          }
          listingViewOpen={Boolean(listingId)}
        />
      </div>

      {shouldRenderBottomNav && (
        <Navigation
          activeTab={navActiveTab}
          onSelect={handleNavSelect}
          activityCount={activityData?.total_new ?? 0}
          messagesCount={newMessagesCount}
          toast={
            interestToast !== null ? (
              // Keyed on the interest so a newer one replaces the bubble and
              // restarts its countdown, rather than inheriting the old timer.
              <ActivityToast
                key={interestToast.pending.id}
                count={interestToast.count}
                onOpen={() => {
                  dismissInterestToast();
                  handleNavSelect('activity');
                }}
                onDismiss={dismissInterestToast}
              />
            ) : undefined
          }
        />
      )}
      {shouldRenderBottomNav && (
        <ToolsSheet
          open={toolsSheetOpen}
          slideDir={navSlideDir}
          onClose={() => setToolsSheetOpen(false)}
          onEstimate={() => setEstimateFlowOpen(true)}
          onStylePhotos={handleOpenAddMyPlaceUnseeded}
          onBoost={() => setBoostFlowOpen(true)}
        />
      )}
      {shouldRenderBottomNav && toolsSheetOpen && (
        <>
          <BoostFlow
            open={boostFlowOpen}
            onClose={() => setBoostFlowOpen(false)}
            onGoToSell={() => {
              setBoostFlowOpen(false);
              setToolsSheetOpen(false);
              handleOpenAddMyPlaceUnseeded();
            }}
            userEmail={userEmail}
            isLoggedIn={isLoggedIn}
            setLoginModalVisible={setLoginModalVisible}
          />
          <EstimateFlow
            open={estimateFlowOpen}
            onClose={() => setEstimateFlowOpen(false)}
            mapCenter={mapCenter}
            mapZoom={15.5}
            countryOfFeedContent={countryOfFeedContent || 'Finland'}
            isLoggedIn={isLoggedIn}
            setLoginModalVisible={setLoginModalVisible}
            onGoToSell={() => {
              setEstimateFlowOpen(false);
              setToolsSheetOpen(false);
              handleOpenAddMyPlaceUnseeded();
            }}
            onStarted={() => {
              setEstimateFlowOpen(false);
              setToolsSheetOpen(false);
              history.push('/profile/price-estimations');
            }}
          />
        </>
      )}
      {/* Kept mounted under an open single-listing view (which hides the nav)
          so closing the listing returns to the activity row it was opened
          from rather than to the section list. Open Messages covers it the
          same way, however it opened (Ask more and Open chat on a row among
          them), so closing Messages returns to that row (JAA-443). */}
      {(shouldRenderBottomNav || (activityViewOpen && isListingRoute)) && (
        <ActivityView
          open={activityViewOpen}
          covered={isListingRoute || messagesModalVisible}
          slideDir={navSlideDir}
          onClose={() => setActivityViewOpen(false)}
          onNavigateExplore={handleActivityExploreMap}
          data={activityData}
          isLoading={isLoadingActivity}
          isLoggedIn={isLoggedIn}
          setLoginModalVisible={setLoginModalVisible}
          initialRow={activityDeepLink}
          onInitialRowConsumed={consumeActivityDeepLink}
          onRowClick={(section, entityId) => {
            // Buying rows are cleared only after a page of matches is received.
            if (section === 'buying_interests') return;
            markActivitySeen(section, entityId).catch(() => undefined);
            setActivityData((prev) => clearRowLocally(prev, section, entityId));
          }}
          onCouldSell={() =>
            window.dispatchEvent(new CustomEvent('jaakko:open-add-my-place'))
          }
          onCouldBuy={() =>
            window.dispatchEvent(
              new CustomEvent('jaakko:open-could-buy', {
                detail: {
                  buildingId: '',
                  coordinates: null,
                  address: {
                    street: '',
                    houseNumber: '',
                    city: '',
                    country: '',
                  },
                },
              }),
            )
          }
        />
      )}
    </div>
  );
};

export default AppContent;
